import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client';
import { AuthLayout } from '../components/AuthLayout';
import { Alert, LoadingBlock } from '../components/ui';
import { useAuth } from '../context/AuthContext';

/**
 * Bekreft e-postadressen, fra lenken i e-posten.
 *
 * Tokenet står i spørrestrengen, som på siden for nytt passord. Bekreftelsen
 * skjer med én gang siden åpnes – det finnes ikke noe å fylle ut – og krever
 * ikke innlogging. Er man logget inn i samme nettleser, hentes kontoen på nytt
 * etterpå, så båndet i appen forsvinner uten at man må laste siden.
 */
export function BekreftEpostPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { status, refresh } = useAuth();

  const [tilstand, setTilstand] = useState<'jobber' | 'ok' | 'feil'>(token ? 'jobber' : 'feil');
  const [feil, setFeil] = useState<string | null>(
    token
      ? null
      : 'Vi fant ingen lenke i adressen. Kopierte du den fra e-posten, kan den ha blitt delt over to linjer.',
  );
  // Én gang per visning. React kjører effekter to ganger i utviklingsmodus, og
  // selv om serveren tåler det, er ett kall det riktige.
  const sendt = useRef(false);

  useEffect(() => {
    if (!token || sendt.current) return;
    sendt.current = true;
    api
      .post('/auth/verify-email', { token })
      .then(() => {
        setTilstand('ok');
        void refresh().catch(() => {
          /* Bekreftelsen er gjort; et bånd som henger igjen til neste lasting, er ingen skade. */
        });
      })
      .catch((caught: unknown) => {
        setTilstand('feil');
        setFeil(caught instanceof ApiError ? caught.message : 'Noe gikk galt. Prøv igjen.');
      });
  }, [token, refresh]);

  const innlogget = status === 'authenticated';

  if (tilstand === 'jobber') {
    return (
      <AuthLayout title="Bekrefter adressen" subtitle="Et øyeblikk …">
        <LoadingBlock label="Bekrefter …" />
      </AuthLayout>
    );
  }

  if (tilstand === 'ok') {
    return (
      <AuthLayout
        title="E-postadressen er bekreftet"
        subtitle="Takk! Nå vet vi at vi når fram, også den dagen du trenger et nytt passord."
      >
        {innlogget ? (
          <Link className="btn btn-primary btn-block" to="/app">
            Til planleggingen
          </Link>
        ) : (
          <Link className="btn btn-primary btn-block" to="/logg-inn">
            Logg inn
          </Link>
        )}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Lenken virker ikke" subtitle="Adressen ble ikke bekreftet.">
      <Alert>{feil}</Alert>
      <p className="small">
        {innlogget ? (
          <>
            Du kan be om en ny lenke fra båndet øverst i appen.{' '}
            <Link to="/app">Til planleggingen</Link>
          </>
        ) : (
          <>
            Logg inn, så kan du be om en ny lenke fra båndet øverst i appen.{' '}
            <Link to="/logg-inn">Logg inn</Link>
          </>
        )}
      </p>
    </AuthLayout>
  );
}
